import { HOST_QUERY_KEY, iamApiInstance, type ServiceAppState } from '@kc-console/context';
import { STATUS_CODE } from '@kc-console/core';

import { CookieKeyMap } from '../../constants/cookie';
import { commonGuardErrorHandler } from '../../utils/commonGuardErrorHandler';
import { parseCookies } from '../../utils/cookie/parseCookies';
import { getClientIp } from '../../utils/getClientIp';
import { getRequestUrlData } from '../../utils/getRequestUrlData';

import type { Guard } from '../../types';

/**
 * 쿠키에 있는 KC_AUT 토큰을 이용해 토큰을 재발급받습니다.
 * @description
 * - 쿠키에 KC_AUT 토큰이 없는 경우 401 에러와 동일하게 처리합니다.
 * - KC_AUT 토큰을 이용해 사용자 정보를 가져옵니다. (세션 연장)
 * - default_project_id 를 state.projectId 에 저장합니다.
 *
 */
export const generateBaseTokenGuard: Guard<ServiceAppState> = async ({
  queryClient,
  state,
  context,
}) => {
  const clientIp = getClientIp(context);
  const { cookie } = getRequestUrlData(context);
  const cookies = parseCookies(cookie || '');
  const cookieToken = cookies[CookieKeyMap.TOKEN_KEY] || '';

  if (!cookieToken) {
    // cookie 에 KC_AUT 가 없는 경우는 무조건 401 과 동일하게 로그인 페이지로 리다이렉트 처리
    return commonGuardErrorHandler({
      context,
      state,
      error: { status: STATUS_CODE.UNAUTHORIZED },
    });
  }

  const { fetchCloudUserInfo } = iamApiInstance({
    token: cookieToken,
  });

  try {
    const { authToken, user, project } = await queryClient.fetchQuery({
      queryKey: [HOST_QUERY_KEY.GET_IAM_ACCESS_TOKEN],
      queryFn: () => fetchCloudUserInfo({ clientIp }),
    });

    return {
      token: authToken,
      domainId: user.domain?.id || '',
      domainName: user.domain?.name || '',
      projectId: project?.id || '',
      projectName: project?.name || '',
      defaultProjectId: user.default_project_id,
      status: STATUS_CODE.OK,
      userId: user.id,
      userName: user.name,
    };
  } catch (error) {
    return commonGuardErrorHandler({
      context,
      state,
      error,
    });
  }
};

generateBaseTokenGuard.displayName = 'generateBaseTokenGuard';
generateBaseTokenGuard.traceCategory = 'authentication';
